import React, { useEffect, useMemo, useState } from 'react'
import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography, Skeleton } from '@mui/material'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import IntelLayout from './components/IntelLayout'
import KpiCard from './components/KpiCard'
import Badge from './components/Badge'
import IntelTable from './components/IntelTable'
import { intelApi } from './api'
import { intelColors, formatPct, formatNumber, formatDelta, trendIcon } from './components/intelTheme'

const DIRECTIONS = [
  { val: 'ALL', label: 'All' },
  { val: 'FORWARD', label: 'Forward' },
  { val: 'REVERSE', label: 'Reverse' },
]
const MODES = [
  { val: 'ALL', label: 'All' },
  { val: 'SURFACE', label: 'Surface' },
  { val: 'AIR', label: 'Air' },
]
const SIZES = [
  { val: 'ALL', label: 'All' },
  { val: 'REGULAR', label: 'Regular' },
  { val: 'LARGE', label: 'Large' },
]
const QCS = [
  { val: 'ALL', label: 'All' },
  { val: 'NON_QC', label: 'Non-QC' },
  { val: 'WITH_QC', label: 'With QC' },
]
const VIEWS = [
  { val: 'STYPE', label: 'Service Type' },
  { val: 'COURIER', label: 'Courier' },
  { val: 'CODE', label: 'Courier Code' },
]
const PERIODS = [
  { val: 'DOD', label: 'Day-on-Day' },
  { val: 'WOW', label: 'Week-on-Week' },
  { val: 'MOM', label: 'Month-on-Month' },
]
const SVCVIEWS = [
  { val: 'overview', label: 'Overview' },
  { val: 'cuts', label: 'Cuts' },
]
const dimensionColor = (d) => {
  switch (d) {
    case 'DIRECTION':
      return 'blue'
    case 'MODE':
      return 'purple'
    case 'SIZE':
      return 'orange'
    case 'REVERSE_QC':
      return 'orange'
    case 'SDD_NDD':
      return 'green'
    case 'HIGH_VALUE':
      return 'purple'
    case 'HYPERLOCAL':
      return 'grey'
    default:
      return 'grey'
  }
}

function ChipGroup({ label, options, value, onChange }) {
  return (
    <Stack direction='row' spacing={1} alignItems='center'>
      <Typography sx={{ fontSize: 11, color: '#888', fontWeight: 600 }}>{label}</Typography>
      <ToggleButtonGroup
        value={value}
        exclusive
        size='small'
        onChange={(_, v) => v && onChange(v)}
        sx={{
          '& .MuiToggleButton-root': {
            padding: '4px 12px',
            fontSize: 11.5,
            fontWeight: 600,
            textTransform: 'none',
            color: '#555',
            border: '1.5px solid #d0d7de',
            borderRadius: '16px !important',
            ml: '0 !important',
            mr: '4px',
          },
          '& .Mui-selected': {
            background: `${intelColors.primary} !important`,
            color: '#fff !important',
            borderColor: `${intelColors.primary} !important`,
          },
        }}
      >
        {options.map((o) => (
          <ToggleButton key={o.val} value={o.val}>
            {o.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Stack>
  )
}

export default function TrendsOverview() {
  const [filters, setFilters] = useState({
    direction: 'ALL',
    mode: 'ALL',
    size: 'ALL',
    qc: 'ALL',
    viewBy: 'STYPE',
    period: 'DOD',
    windowWeeks: 8,
  })
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)
  // Legend isolate: null = show all; otherwise only this dataKey is shown. Click same again = restore all.
  const [activeSeries, setActiveSeries] = useState(null)
  // Overview / Cuts sub-view (mockup: switchSvcView). Cuts data fetched lazily.
  const [svcView, setSvcView] = useState('overview')
  const [cuts, setCuts] = useState(null)
  const [cutsLoading, setCutsLoading] = useState(false)

  useEffect(() => {
    let cancel = false
    setLoading(true)
    setErr(null)
    intelApi
      .trendsOverview(filters)
      .then((res) => {
        if (cancel) return
        setData(res?.data?.responseObj || res?.data?.data || res?.data)
      })
      .catch((e) => {
        if (!cancel) setErr(e?.response?.data?.exception || e.message)
      })
      .finally(() => !cancel && setLoading(false))
    return () => {
      cancel = true
    }
  }, [filters])

  useEffect(() => {
    if (svcView !== 'cuts') return undefined
    let cancel = false
    setCutsLoading(true)
    intelApi
      .trendsCuts({ windowWeeks: filters.windowWeeks })
      .then((res) => {
        if (!cancel) setCuts(res?.data?.responseObj || res?.data?.data || res?.data)
      })
      .catch(() => {})
      .finally(() => !cancel && setCutsLoading(false))
    return () => {
      cancel = true
    }
  }, [svcView, filters.windowWeeks])

  const kpis = data?.kpis || {}
  const breakdown = data?.breakdown || []

  const chartData = useMemo(() => {
    const points = data?.sparklinePoints || []
    return points.map((p) => ({
      date: p.date,
      value: p.value,
      forwardValue: p.forwardValue,
      reverseValue: p.reverseValue,
    }))
  }, [data])

  const setF = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }))

  const columns = useMemo(() => {
    if (filters.viewBy === 'COURIER') {
      return [
        { key: 'parent', header: 'Courier Partner', render: (r) => <strong>{r.parent || r.label}</strong> },
        { key: 'activeCodes', header: 'Active Codes', render: (r) => r.activeCodes ?? '—' },
        { key: 'todayPct', header: 'Serviceability %', render: (r) => formatPct(r.todayPct) },
        {
          key: 'wow',
          header: 'WoW Δ',
          render: (r) => {
            const d = formatDelta(r.wowDeltaPct)
            return <span style={{ color: d.color }}>{d.text}</span>
          },
        },
        {
          key: 'mom',
          header: 'MoM Δ',
          render: (r) => {
            const d = formatDelta(r.momDeltaPct)
            return <span style={{ color: d.color }}>{d.text}</span>
          },
        },
        { key: 'fwd', header: 'Forward', render: (r) => formatPct(r.fwdPct) },
        { key: 'rev', header: 'Reverse', render: (r) => formatPct(r.revPct) },
        { key: 'surf', header: 'Surface', render: (r) => formatPct(r.surfacePct) },
        { key: 'air', header: 'Air', render: (r) => formatPct(r.airPct) },
      ]
    }
    if (filters.viewBy === 'CODE') {
      return [
        { key: 'code', header: 'Courier Code', render: (r) => <code style={{ background: '#f1f3f5', padding: '1px 5px', borderRadius: 3, fontSize: 11 }}>{r.cpAccountCode || r.key}</code> },
        { key: 'parent', header: 'Parent Courier', render: (r) => r.parent },
        { key: 'stype', header: 'Service Type', render: (r) => r.serviceTypeName || r.serviceTypePrimary },
        { key: 'today', header: 'Serviceability %', render: (r) => formatPct(r.todayPct) },
        {
          key: 'wow',
          header: 'WoW Δ',
          render: (r) => {
            const d = formatDelta(r.wowDeltaPct)
            return <span style={{ color: d.color }}>{d.text}</span>
          },
        },
        {
          key: 'mom',
          header: 'MoM Δ',
          render: (r) => {
            const d = formatDelta(r.momDeltaPct)
            return <span style={{ color: d.color }}>{d.text}</span>
          },
        },
        { key: 'pins', header: 'Pincodes Active', render: (r) => formatNumber(r.pincodesActive) },
        {
          key: 'status',
          header: 'Status',
          render: (r) => {
            const s = (r.status || '').toLowerCase()
            // mockup: Active=green, Watch=orange, Sync pending=grey
            const color = s.includes('active') ? 'green' : s.includes('watch') ? 'orange' : 'grey'
            return <Badge color={color}>{r.status || '—'}</Badge>
          },
        },
      ]
    }
    return [
      { key: 'name', header: 'Service Type', render: (r) => <strong>{r.label || r.key}</strong> },
      {
        key: 'dir',
        header: 'Direction',
        render: (r) => <Badge color={r.direction === 'RVP' ? 'orange' : 'blue'}>{r.directionLabel || r.direction || '—'}</Badge>,
      },
      {
        key: 'mode',
        header: 'Mode',
        render: (r) => <Badge color={r.mode === 'AIR' ? 'purple' : 'blue'}>{r.mode || '—'}</Badge>,
      },
      { key: 'today', header: 'Today', render: (r) => formatPct(r.todayPct) },
      {
        key: 'wow',
        header: 'WoW Δ',
        render: (r) => {
          const d = formatDelta(r.wowDeltaPct)
          return <span style={{ color: d.color }}>{d.text}</span>
        },
      },
      {
        key: 'mom',
        header: 'MoM Δ',
        render: (r) => {
          const d = formatDelta(r.momDeltaPct)
          return <span style={{ color: d.color }}>{d.text}</span>
        },
      },
      { key: 'peak', header: 'Peak (30d)', render: (r) => formatPct(r.peakPct) },
      { key: 'trough', header: 'Trough (30d)', render: (r) => formatPct(r.troughPct) },
      { key: 'trend', header: 'Trend', render: (r) => trendIcon(r.trendDirection) },
    ]
  }, [filters.viewBy])

  const cutsColumns = [
    { key: 'dim', header: 'Dimension', render: (r) => <Badge color={dimensionColor(r.dimension)}>{r.dimension}</Badge> },
    { key: 'cut', header: 'Cut', render: (r) => <strong>{r.cutDisplayName || r.cut}</strong> },
    { key: 'pins', header: 'Pincodes Serviceable', render: (r) => formatNumber(r.pinsServiceable) },
    { key: 'pct', header: '% of Universe', render: (r) => formatPct(r.pctOfUniverse) },
    {
      key: 'wow',
      header: 'WoW Δ',
      render: (r) => {
        const d = formatDelta(r.wowDeltaPins, '', 0)
        return <span style={{ color: d.color }}>{d.text} pincodes</span>
      },
    },
    {
      key: 'mom',
      header: 'MoM Δ',
      render: (r) => {
        const d = formatDelta(r.momDeltaPins, '', 0)
        return <span style={{ color: d.color }}>{d.text} pincodes</span>
      },
    },
    { key: 'trend', header: 'Trend', render: (r) => trendIcon(r.trendDirection) },
  ]

  return (
    <IntelLayout title='Overall Serviceability'>
      {/* KPIs */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px', mb: '18px' }}>
        <KpiCard loading={loading}
          color='blue'
          label='Serviceable Pincodes Today'
          value={loading ? <Skeleton width={80} /> : formatNumber(kpis.servicableToday?.value)}
          sub={kpis.servicableToday ? `${formatPct(kpis.servicableToday.pctOfUniverse)} of ${formatNumber(data?.universePinCount)}` : ''}
          subColor={intelColors.green}
        />
        <KpiCard loading={loading}
          color='green'
          label='Forward Coverage'
          value={loading ? <Skeleton width={80} /> : formatNumber(kpis.forwardCoverage?.value)}
          sub={kpis.forwardCoverage ? `WoW Δ ${formatNumber(kpis.forwardCoverage.wowDelta)} · ${formatPct(kpis.forwardCoverage.pctOfUniverse)}` : ''}
          subColor={intelColors.green}
        />
        <KpiCard loading={loading}
          color='orange'
          label='Reverse Coverage'
          value={loading ? <Skeleton width={80} /> : formatNumber(kpis.reverseCoverage?.value)}
          sub={kpis.reverseCoverage ? `WoW Δ ${formatNumber(kpis.reverseCoverage.wowDelta)} · ${formatPct(kpis.reverseCoverage.pctOfUniverse)}` : ''}
          subColor={intelColors.green}
        />
        <KpiCard loading={loading}
          color='purple'
          label='Surface vs Air Gap'
          value={loading ? <Skeleton width={80} /> : `${formatNumber(kpis.surfaceAirGap?.value)} pincodes`}
          sub={kpis.surfaceAirGap ? `Surface ${formatNumber(kpis.surfaceAirGap.surfaceValue)} · Air ${formatNumber(kpis.surfaceAirGap.airValue)}` : ''}
        />
      </Box>

      {/* Overview / Cuts toggle (mockup: switchSvcView) */}
      <Box sx={{ display: 'flex', gap: 1, mb: '14px' }}>
        {SVCVIEWS.map((v) => (
          <Box
            key={v.val}
            onClick={() => setSvcView(v.val)}
            sx={{
              padding: '6px 16px',
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 700,
              borderRadius: '16px',
              border: `1.5px solid ${svcView === v.val ? intelColors.primary : '#d0d7de'}`,
              color: svcView === v.val ? '#fff' : '#555',
              background: svcView === v.val ? intelColors.primary : '#fff',
            }}
          >
            {v.label}
          </Box>
        ))}
      </Box>

      {svcView === 'overview' && (
        <>
      {/* Filter bar */}
      <Box
        sx={{
          background: '#f8f9fa',
          border: '1px solid #e1e4e8',
          borderRadius: '8px',
          padding: '14px 16px',
          mb: '16px',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
        }}
      >
        <ChipGroup label='Direction' options={DIRECTIONS} value={filters.direction} onChange={setF('direction')} />
        <ChipGroup label='Mode' options={MODES} value={filters.mode} onChange={setF('mode')} />
        <ChipGroup label='Size' options={SIZES} value={filters.size} onChange={setF('size')} />
        <ChipGroup label='RVP QC' options={QCS} value={filters.qc} onChange={setF('qc')} />
        <Box sx={{ ml: 'auto' }}>
          <ChipGroup label='View by' options={VIEWS} value={filters.viewBy} onChange={setF('viewBy')} />
        </Box>
      </Box>

      {/* Period tabs + chart */}
      <Box
        sx={{
          background: '#fff',
          border: '1px solid #e1e4e8',
          borderRadius: '8px',
          overflow: 'hidden',
          mb: '18px',
        }}
      >
        <Box sx={{ display: 'flex', borderBottom: '1px solid #e1e4e8', px: '16px' }}>
          {PERIODS.map((p) => (
            <Box
              key={p.val}
              onClick={() => setF('period')(p.val)}
              sx={{
                padding: '8px 18px',
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
                color: filters.period === p.val ? intelColors.primary : '#555',
                borderBottom: `2px solid ${filters.period === p.val ? intelColors.primary : 'transparent'}`,
              }}
            >
              {p.label}
            </Box>
          ))}
        </Box>
        <Box sx={{ p: '16px', height: 260 }}>
          <ResponsiveContainer width='100%' height='100%'>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray='3 3' stroke='#f0f0f0' />
              <XAxis dataKey='date' tick={{ fontSize: 11, fill: '#637381' }} />
              <YAxis tick={{ fontSize: 11, fill: '#637381' }} />
              <Tooltip />
              <Legend
                onClick={(o) => setActiveSeries((cur) => (cur === o.dataKey ? null : o.dataKey))}
                wrapperStyle={{ cursor: 'pointer' }}
              />
              <Line type='monotone' dataKey='forwardValue' stroke={intelColors.green} strokeWidth={2} dot={{ r: 2 }} name='Forward' hide={activeSeries !== null && activeSeries !== 'forwardValue'} />
              <Line type='monotone' dataKey='reverseValue' stroke={intelColors.orange} strokeWidth={2} dot={{ r: 2 }} name='Reverse' hide={activeSeries !== null && activeSeries !== 'reverseValue'} />
              <Line type='monotone' dataKey='value' stroke={intelColors.primary} strokeWidth={2.5} strokeDasharray='5 3' dot={{ r: 2 }} name='Overall' hide={activeSeries !== null && activeSeries !== 'value'} />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </Box>

      {err && (
        <Box sx={{ background: '#ffebee', border: '1px solid #ef9a9a', color: '#c62828', borderRadius: '6px', p: '10px 14px', mb: '14px', fontSize: 12 }}>
          {err}
        </Box>
      )}

      <IntelTable
        title={`Serviceability by ${VIEWS.find((v) => v.val === filters.viewBy)?.label}`}
        loading={loading}
        columns={columns}
        rows={breakdown}
        rowKey={(r, i) => r.key || r.cpAccountCode || r.parent || i}
        pageSize={50}
      />
        </>
      )}

      {svcView === 'cuts' && (
        <>
          <Box sx={{ fontSize: 12, color: intelColors.muted, mb: '12px' }}>
            Serviceability by dimension cut — all cuts in one view. Universe: {formatNumber(cuts?.universePinCount)} pincodes · As of {cuts?.asOfDate || '—'}
          </Box>
          <IntelTable
            title='Serviceability by Dimension Cut'
            loading={cutsLoading}
            columns={cutsColumns}
            rows={cuts?.cuts || []}
            rowKey={(r, i) => `${r.dimension}-${r.cut}-${i}`}
            pageSize={50}
          />
        </>
      )}
    </IntelLayout>
  )
}
